/**
 * iCure Data Stack API Documentation
 * The iCure Data Stack Application API is the native interface to iCure. This version is obsolete, please use v2.
 *
 * OpenAPI spec version: v1
 *
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
import { XHR } from './XHR'
import { DocIdentifier } from '../model/DocIdentifier'
import { Receipt } from '../model/Receipt'
import { AuthenticationProvider, NoAuthenticationProvider } from '../../icc-x-api/auth/AuthenticationProvider'
import { iccRestApiPath } from './IccRestApiPath'
import { EntityShareOrMetadataUpdateRequest } from '../model/requests/EntityShareOrMetadataUpdateRequest'
import { EntityBulkShareResult } from '../model/requests/EntityBulkShareResult'
import { ListOfIds } from '../model/ListOfIds'
import { BulkShareOrUpdateMetadataParams } from '../model/requests/BulkShareOrUpdateMetadataParams'
import { ConflictResolutionRequest } from '../model/ConflictResolutionRequest'
import { ConflictResolutionResult } from '../model/ConflictResolutionResult'
import { ConflictResolutionStrategy } from '../model/ConflictResolutionStrategy'
import { MergeResult } from '../model/MergeResult'

export class IccReceiptApi {
  host: string
  _headers: Array<XHR.Header>
  authenticationProvider: AuthenticationProvider
  fetchImpl?: (input: RequestInfo, init?: RequestInit) => Promise<Response>

  get headers(): Promise<Array<XHR.Header>> {
    return Promise.resolve(this._headers)
  }

  constructor(
    host: string,
    headers: any,
    authenticationProvider?: AuthenticationProvider,
    fetchImpl?: (input: RequestInfo, init?: RequestInit) => Promise<Response>
  ) {
    this.host = iccRestApiPath(host)
    this._headers = Object.keys(headers).map((k) => new XHR.Header(k, headers[k]))
    this.authenticationProvider = !!authenticationProvider ? authenticationProvider : new NoAuthenticationProvider()
    this.fetchImpl = fetchImpl
  }

  setHeaders(h: Array<XHR.Header>) {
    this._headers = h
  }

  handleError(e: XHR.XHRError): never {
    throw e
  }

  /**
   *
   * @summary Creates a receipt
   * @param body
   */
  async createReceipt(body?: Receipt): Promise<Receipt> {
    const _url = this.host + `/receipt` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Receipt(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * @summary Deletes a batch of receipts.
   *
   * @param receiptIds a ListOfIds containing the ids of the receipts to delete.
   * @return a Promise that will resolve in an array of DocIdentifiers of the successfully deleted receipts.
   */
  async deleteReceipts(receiptIds: ListOfIds): Promise<Array<DocIdentifier>> {
    const headers = (await this.headers).filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand(
      'POST',
      this.host + `/receipt/delete/batch` + '?ts=' + new Date().getTime(),
      headers,
      receiptIds,
      this.fetchImpl,
      undefined,
      this.authenticationProvider.getAuthService()
    )
      .then((doc) => (doc.body as Array<JSON>).map((it) => new DocIdentifier(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * @summary Deletes a single receipt by id.
   *
   * @param receiptId the id of the receipt to delete.
   * @return a Promise that will resolve in the DocIdentifier of the deleted receipt.
   */
  async deleteReceipt(receiptId: string): Promise<DocIdentifier> {
    return XHR.sendCommand(
      'DELETE',
      this.host + `/receipt/${encodeURIComponent(receiptId)}` + '?ts=' + new Date().getTime(),
      await this.headers,
      null,
      this.fetchImpl,
      undefined,
      this.authenticationProvider.getAuthService()
    )
      .then((doc) => new DocIdentifier(doc.body))
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Gets a receipt
   * @param receiptId
   */
  async getReceipt(receiptId: string): Promise<Receipt> {
    let _body = null

    const _url = this.host + `/receipt/${encodeURIComponent(String(receiptId))}` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Receipt(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Get an attachment
   * @param receiptId
   * @param attachmentId
   * @param enckeys
   */
  async getReceiptAttachment(receiptId: string, attachmentId: string, enckeys?: null): Promise<ArrayBuffer> {
    if (enckeys) {
      throw new Error('Server-side encryption is not supported anymore.')
    }
    let _body = null

    const _url =
      this.host +
      `/receipt/${encodeURIComponent(String(receiptId))}/attachment/${encodeURIComponent(String(attachmentId))}` +
      '?ts=' +
      new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => doc.body)
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Gets a receipt
   * @param ref
   */
  async listByReference(ref: string): Promise<Array<Receipt>> {
    let _body = null

    const _url = this.host + `/receipt/byRef/${encodeURIComponent(String(ref))}` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new Receipt(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Lists the receipts created within the provided date interval, based on the `created` timestamp of the receipt.
   * Both bounds are inclusive and optional: an undefined bound leaves that side of the interval open.
   * @summary List the receipts created within a date interval
   * @param startDate the start of the interval as a unix epoch in ms (inclusive), no lower bound if undefined.
   * @param endDate the end of the interval as a unix epoch in ms (inclusive), no upper bound if undefined.
   * @param descending whether to sort the result from the most recently created receipt to the oldest.
   * @return the receipts created within the provided interval.
   */
  async listReceiptsBetweenDates(startDate?: number, endDate?: number, descending?: boolean): Promise<Array<Receipt>> {
    const _url =
      this.host +
      `/receipt/byCreated` +
      '?ts=' +
      new Date().getTime() +
      (startDate !== undefined ? '&startDate=' + encodeURIComponent(String(startDate)) : '') +
      (endDate !== undefined ? '&endDate=' + encodeURIComponent(String(endDate)) : '') +
      (descending !== undefined ? '&descending=' + encodeURIComponent(String(descending)) : '')
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, null, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new Receipt(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   *
   * @summary Updates a receipt
   * @param body
   */
  async modifyReceipt(body?: Receipt): Promise<Receipt> {
    let _body = null
    _body = body

    const _url = this.host + `/receipt` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('PUT', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Receipt(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * @deprecated use setReceiptAttachmentForBlobType instead
   */
  async setReceiptAttachment(receiptId: string, receiptRev: string, blobType: string, enckeys?: null, body?: ArrayBuffer): Promise<Receipt> {
    if (enckeys) {
      throw new Error('Server-side encryption is not supported anymore.')
    }
    if (!body) {
      throw new Error('Attachment content is requred')
    }
    return this.setReceiptAttachmentForBlobType(receiptId, receiptRev, blobType, body)
  }

  /**
   * @summary Creates a receipt's attachment
   * @param receiptId id of a receipt
   * @param receiptRev rev of the receipt
   * @param blobType receipt blob type
   * @param body content of the attachment, already encrypted
   */
  async setReceiptAttachmentForBlobType(receiptId: string, receiptRev: string, blobType: string, body: ArrayBuffer): Promise<Receipt> {
    if (!receiptRev) throw new Error('Receipt rev is required')

    let _body = body

    const _url =
      this.host +
      `/receipt/${encodeURIComponent(String(receiptId))}/attachment/${encodeURIComponent(String(blobType))}` +
      '?ts=' +
      new Date().getTime() +
      '&rev=' +
      receiptRev +
      '&contentLength=' +
      encodeURIComponent(String(body.byteLength)) +
      '&contentType=' +
      encodeURIComponent('application/octet-stream')
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/octet-stream'))
    return XHR.sendCommand('PUT', _url, headers, _body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Receipt(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * @summary Creates a receipt's data attachment with compression metadata
   * @param receiptId id of a receipt
   * @param blobType receipt blob type
   * @param rev revision of the receipt
   * @param body content of the attachment
   * @param compressionAlgorithm the compression algorithm used on the client side, if any
   * @param triedCompressionAlgorithmsVersion a version string for the compression algorithms tried
   * @param realDataSize the real size of the data after decryption and decompression
   */
  async setReceiptDataAttachment(
    receiptId: string,
    blobType: string,
    rev: string,
    body: ArrayBuffer,
    compressionAlgorithm?: string,
    triedCompressionAlgorithmsVersion?: string,
    realDataSize?: number
  ): Promise<Receipt> {
    if (!rev) throw new Error('Receipt rev is required')

    const _url =
      this.host +
      `/receipt/${encodeURIComponent(String(receiptId))}/dataattachment/${encodeURIComponent(String(blobType))}` +
      '?ts=' +
      new Date().getTime() +
      '&rev=' +
      encodeURIComponent(String(rev)) +
      (compressionAlgorithm ? '&compressionAlgorithm=' + encodeURIComponent(String(compressionAlgorithm)) : '') +
      (triedCompressionAlgorithmsVersion
        ? '&triedCompressionAlgorithmsVersion=' + encodeURIComponent(String(triedCompressionAlgorithmsVersion))
        : '') +
      (realDataSize != null ? '&realDataSize=' + encodeURIComponent(String(realDataSize)) : '') +
      '&contentLength=' +
      encodeURIComponent(String(body.byteLength))
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/octet-stream'))
    return XHR.sendCommand('PUT', _url, headers, body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new Receipt(doc.body as JSON))
      .catch((err) => this.handleError(err))
  }

  /**
   * @summary Get a receipt's attachment by blob type
   * @param receiptId id of the receipt
   * @param blobType the blob type of the attachment to retrieve
   */
  async getReceiptAttachmentByBlobType(receiptId: string, blobType: string): Promise<ArrayBuffer> {
    const _url =
      this.host +
      `/receipt/${encodeURIComponent(String(receiptId))}/attachment/ofType/${encodeURIComponent(String(blobType))}` +
      '?ts=' +
      new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, null, this.fetchImpl, 'application/octet-stream', this.authenticationProvider.getAuthService())
      .then((doc) => doc.body)
      .catch((err) => this.handleError(err))
  }

  /**
   * @internal this method is for internal use only and may be changed without notice
   */
  async bulkShareReceipt(request: BulkShareOrUpdateMetadataParams): Promise<EntityBulkShareResult<Receipt>[]> {
    const _url = this.host + '/receipt/bulkSharedMetadataUpdate' + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('PUT', _url, headers, request, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((x) => new EntityBulkShareResult<Receipt>(x, Receipt)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Retrieves the ids of all the receipts that currently have conflicting revisions and therefore need to
   * be resolved.
   * @summary List the ids of the receipts that have conflicts.
   * @return the ids of the receipts with unresolved conflicts.
   */
  async getConflictingEntitiesIds(): Promise<Array<string>> {
    const _url = this.host + `/receipt/conflicts` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, null, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => doc.body as Array<string>)
      .catch((err) => this.handleError(err))
  }

  /**
   * Retrieves all the conflicting revisions of the receipt with the given id (the current revision is not
   * included). The returned entities are still encrypted.
   * @summary Get the conflicting revisions of a receipt.
   * @param entityId the id of the receipt to retrieve the conflicts for.
   * @return the conflicting revisions of the receipt.
   */
  async getConflictsForEntity(entityId: string): Promise<Array<Receipt>> {
    const _url = this.host + `/receipt/conflicts/of?entityId=${encodeURIComponent(String(entityId))}` + '&ts=' + new Date().getTime()
    let headers = await this.headers
    return XHR.sendCommand('GET', _url, headers, null, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new Receipt(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Resolves a conflict by declaring which revision of the receipt should be kept as winner and which
   * conflicting revisions should be purged. The provided document must already be encrypted.
   * @summary Declare the winning revision of a conflicting receipt.
   * @param body the {@link ConflictResolutionRequest} carrying the winning revision and the conflicts to purge.
   * @return the {@link ConflictResolutionResult} with the saved winner and the conflicts that are still unresolved.
   */
  async declareConflictWinner(body: ConflictResolutionRequest<Receipt>): Promise<ConflictResolutionResult<Receipt>> {
    const _url = this.host + `/receipt/conflicts/winner` + '?ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, body, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => new ConflictResolutionResult<Receipt>(doc.body, (x) => new Receipt(x)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Automatically resolves the conflicting revisions of the receipts with the given ids, according to the provided
   * {@link ConflictResolutionStrategy}.
   * @summary Automatically solve the conflicts of the receipts with the given ids.
   * @param entityIds the ids of the receipts to solve the conflicts for.
   * @param strategy the {@link ConflictResolutionStrategy} to use to resolve the conflicts. Defaults to `FullMergeability`.
   * @return the {@link MergeResult} of the conflict resolution for each requested entity.
   */
  async autoSolveConflicts(
    entityIds: Array<string>,
    strategy: ConflictResolutionStrategy = ConflictResolutionStrategy.FullMergeability
  ): Promise<Array<MergeResult>> {
    const _url = this.host + `/receipt/conflicts/solve?strategy=${encodeURIComponent(String(strategy))}` + '&ts=' + new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, entityIds, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new MergeResult(it)))
      .catch((err) => this.handleError(err))
  }

  /**
   * Like {@link autoSolveConflicts} but targets the entities of the group with the given id.
   * @summary Automatically solve the conflicts of the receipts with the given ids, in the given group.
   * @param groupId the id of the group to look into.
   * @param entityIds the ids of the receipts to solve the conflicts for.
   * @param strategy the {@link ConflictResolutionStrategy} to use to resolve the conflicts. Defaults to `FullMergeability`.
   * @return the {@link MergeResult} of the conflict resolution for each requested entity.
   */
  async autoSolveConflictsInGroup(
    groupId: string,
    entityIds: Array<string>,
    strategy: ConflictResolutionStrategy = ConflictResolutionStrategy.FullMergeability
  ): Promise<Array<MergeResult>> {
    const _url =
      this.host +
      `/receipt/inGroup/${encodeURIComponent(String(groupId))}/conflicts/solve?strategy=${encodeURIComponent(String(strategy))}` +
      '&ts=' +
      new Date().getTime()
    let headers = await this.headers
    headers = headers.filter((h) => h.header !== 'Content-Type').concat(new XHR.Header('Content-Type', 'application/json'))
    return XHR.sendCommand('POST', _url, headers, entityIds, this.fetchImpl, undefined, this.authenticationProvider.getAuthService())
      .then((doc) => (doc.body as Array<JSON>).map((it) => new MergeResult(it)))
      .catch((err) => this.handleError(err))
  }
}
